/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable  @typescript-eslint/consistent-indexed-object-style */
/* eslint-disable  sonarjs/array-callback-without-return */
import {FormArray, FormControl, FormGroup, ValidatorFn} from '@angular/forms';
import {TillTwoDigits_Regex} from '../constants';
import {
  EDynamicFormFieldType,
  IDynamicFormCheckbox,
  IDynamicFormFieldType,
  IDynamicFormRadioButton,
} from '../models/dynamic.model';
import {DynamicFormValidationUtils} from './dynamic-form-validation.utils';
import {DynamicFormUtils} from './dynamic-form.utils';

type AccordionExpandValueType = string | number | string[] | number[];

export class DynamicFormBuilderUtils {
  private static _readonly: boolean = false;
  public static get readonly(): boolean {
    return this._readonly;
  }
  public static set readonly(value: boolean) {
    this._readonly = value;
  }

  private static _accordionExpandValue: {
    [key: string]: AccordionExpandValueType;
  } = {};
  public static get accordionExpandValue() {
    return this._accordionExpandValue;
  }
  public static set accordionExpandValue(value: {[key: string]: AccordionExpandValueType}) {
    this._accordionExpandValue = value;
  }

  private static _variableAccordionExpandValue: {
    [key: string]: AccordionExpandValueType;
  } = {};
  public static get variableAccordionExpandValue() {
    return this._variableAccordionExpandValue;
  }
  public static set variableAccordionExpandValue(value: {[key: string]: AccordionExpandValueType}) {
    this._variableAccordionExpandValue = value;
  }

  static buildFormGroup(formTemplateList: IDynamicFormFieldType[], formGroup: FormGroup | FormArray) {
    for (let findex = 0; findex < formTemplateList.length; findex++) {
      const field = formTemplateList[parseInt(`${findex}`)];
      this.addControl(field, formGroup);
    }
  }

  static addControl(field: IDynamicFormFieldType, formGroup: FormGroup | FormArray) {
    if (field.type !== EDynamicFormFieldType.HtmlTag && field.type !== EDynamicFormFieldType.InputGroupAddon) {
      const validations = field.validators ? DynamicFormValidationUtils.buildValidations(field) : [];
      if (field.type === EDynamicFormFieldType.Checkbox) {
        field = DynamicFormUtils.castAsCheckbox(field);
        this.addCheckboxOrRadioButton(field, formGroup);
        this.addField(formGroup, field.name, field.value, this.readonly || !!field.disabled, validations);
      } else if (field.type === EDynamicFormFieldType.RadioButton) {
        field = DynamicFormUtils.castAsRadioButton(field);
        this.addCheckboxOrRadioButton(field, formGroup);
        this.addField(formGroup, field.name, field.value, this.readonly || !!field.disabled, validations);
      } else if (field.type === EDynamicFormFieldType.InputGroup) {
        this.addInputGroup(field, formGroup);
      } else if (
        field.type === EDynamicFormFieldType.Accordion ||
        field.type === EDynamicFormFieldType.VariableAccordion
      ) {
        this.setAccordionExpandValues(field);
        this.addAccordion(field, formGroup);
      } else if (
        field.type === EDynamicFormFieldType.AccordionTab ||
        field.type === EDynamicFormFieldType.VariableAccordionTab
      ) {
        this.addAccordionTab(field, formGroup);
      } else {
        this.addField(formGroup, field.name, field.value, this.readonly || !!field.disabled, validations);
      }
    }
  }

  static addCheckboxOrRadioButton(
    field: IDynamicFormCheckbox | IDynamicFormRadioButton,
    formGroup: FormGroup | FormArray
  ) {
    const options = field.options ?? [];
    for (let oindex = 0; oindex < options.length; oindex++) {
      const option = options[`${oindex}`];
      this.addField(formGroup, `${field.name}_${option.value}`, option.selected, this.readonly || !!field.disabled, []);
    }
  }

  static addInputGroup(field: IDynamicFormFieldType, formGroup: FormGroup | FormArray) {
    const inputGroup = DynamicFormUtils.castAsInputGroup(field);
    const formSubGroup = new FormGroup({});
    for (let ig = 0; ig < inputGroup.fields.length; ig++) {
      const element = inputGroup.fields[parseInt(`${ig}`)];
      this.addControl(element, formSubGroup);
    }
    if (formGroup instanceof FormArray) {
      formGroup.push(formSubGroup);
    } else {
      formGroup.addControl(inputGroup.name, formSubGroup);
    }
  }

  static addAccordion(field: IDynamicFormFieldType, formGroup: FormGroup | FormArray) {
    const accordion = DynamicFormUtils.castAsAccordion(field);
    const formArray = new FormArray([] as any[]);
    for (let acc = 0; acc < accordion.fields.length; acc++) {
      const element = accordion.fields[parseInt(`${acc}`)];
      this.addControl(element, formArray);
    }
    if (formGroup instanceof FormArray) {
      formGroup.push(formArray);
    } else {
      formGroup.addControl(accordion.name, formArray);
    }
  }

  static addAccordionTab(field: IDynamicFormFieldType, formGroup: FormGroup | FormArray) {
    const accordionTab = DynamicFormUtils.castAsAccordionTab(field);
    const formSubGroup = new FormGroup({});
    for (let acc = 0; acc < accordionTab.fields.length; acc++) {
      const element = accordionTab.fields[parseInt(`${acc}`)];
      this.addControl(element, formSubGroup);
    }
    if (formGroup instanceof FormArray) {
      formGroup.push(formSubGroup);
    } else {
      formGroup.addControl(accordionTab.name, formSubGroup);
    }
  }

  static addField(
    formGroup: FormGroup | FormArray,
    key: string,
    value: any,
    disabled: boolean,
    validations: ValidatorFn | ValidatorFn[] | null
  ) {
    if (formGroup instanceof FormArray) {
      formGroup.push(new FormControl({value: value, disabled: disabled}, validations));
    } else {
      formGroup.addControl(key, new FormControl({value: value, disabled: disabled}, validations));
    }
  }

  static setAccordionExpandValues(field: IDynamicFormFieldType) {
    const expandedTabs: number[] = [];
    const accordion = DynamicFormUtils.castAsAccordion(field);
    accordion.fields?.map((x, i) => {
      if (x.type === EDynamicFormFieldType.AccordionTab || x.type === EDynamicFormFieldType.VariableAccordionTab) {
        const at = DynamicFormUtils.castAsAccordionTab(x);
        const isExpanded = at.expanded ?? false;
        if (isExpanded) {
          expandedTabs.push(i);
        }
      }
    });
    if (field.type === EDynamicFormFieldType.Accordion) {
      this.accordionExpandValue[`${field.name}`] = expandedTabs;
    } else {
      this.variableAccordionExpandValue[`${field.name}`] = expandedTabs;
    }
  }

  static removeVariableAccordionTab(dynamicForm: FormGroup, field: IDynamicFormFieldType, index: number) {
    const vaFormArray = dynamicForm.get(field.name) as FormArray;
    const vAccordion = DynamicFormUtils.castAsVariableAccordion(field);
    vAccordion.fields.splice(index, 1);
    vaFormArray.removeAt(index);
  }

  static addVariableAccordionTab(dynamicForm: FormGroup, field: IDynamicFormFieldType) {
    const vaFormArray = dynamicForm.get(field.name) as FormArray;
    const vAccordion = DynamicFormUtils.castAsVariableAccordion(field);
    if (vAccordion.max && vAccordion.fields.length < vAccordion.max) {
      const initialTab = vAccordion.fields[0];
      const nextTabName = initialTab.name.replace(TillTwoDigits_Regex, (vAccordion.fields.length + 1).toString());
      const nextTab = JSON.parse(
        JSON.stringify(initialTab)
          .replaceAll(`"name":"${initialTab.name}.`, '"name":"')
          .replaceAll(`${initialTab.name}`, `${nextTabName}`)
      );

      this.addControl(nextTab, vaFormArray);

      vAccordion.fields.push(nextTab);

      this.setAccordionExpandValues(vAccordion);
      dynamicForm.updateValueAndValidity();
    }
  }
}
